'use client';

import React, { startTransition, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { subscribeNewsLetter } from '@/app/actions/newsletter';
import { apiVerifyCaptcha } from '@/apis/captcha';
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';

interface NewsletterFormComponentProps {
    component: ComponentNode;
    context?: {
        companyId?: string;
        domain?: string;
        store_slug?: string;
    };
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterFormComponent: React.FC<NewsletterFormComponentProps> = ({ component, context }) => {
    return (
        <RecaptchaProvider domain={context?.domain || ''}>
            <NewsletterFormInner component={component} context={context} />
        </RecaptchaProvider>
    );
};

const NewsletterFormInner: React.FC<NewsletterFormComponentProps> = ({
    component,
    context,
}) => {
    const { data, styles, customClassName, placeholder, buttonText, components } = component;
    const inlineStyles = convertStylesToInline(styles);
    // Not a real rendered child — see the doc comment on this node in
    // theme-builder's footerStructure.ts. Its styles carry a Style-tab edit
    // on the "Newsletter button" part, independently of the form/input.
    const buttonNode = components?.find((child: any) => child?.builderPart === 'newsletterButton');
    const buttonStyles = convertStylesToInline(buttonNode?.styles);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<{
        type: 'success' | 'error' | 'info' | null;
        message: string;
    }>({ type: null, message: '' });
    const [isPending, setIsPending] = useState(false);

    const inputPlaceholder =
        typeof data === 'string' && data.trim().length > 0
            ? data
            : typeof placeholder === 'string' && placeholder.trim().length > 0
              ? placeholder
              : 'Enter your email address';

    const submitLabel =
        typeof buttonText === 'string' && buttonText.trim().length > 0
            ? buttonText
            : 'Subscribe';

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!context?.companyId) {
            setStatus({
                type: 'error',
                message: 'Newsletter signup is not configured yet.',
            });
            return;
        }

        if (!emailRegex.test(email.trim())) {
            setStatus({
                type: 'error',
                message: 'Please enter a valid email address.',
            });
            return;
        }

        setIsPending(true);
        setStatus({ type: null, message: '' });

        try {
            if (executeRecaptcha) {
                const token = await executeRecaptcha('newsletter_subscribe');
                const verification = await apiVerifyCaptcha(context.domain || '', token);
                if (!verification?.verified) {
                    setStatus({
                        type: 'error',
                        message: 'Captcha verification failed. Please try again.',
                    });
                    setIsPending(false);
                    return;
                }
            }
        } catch {
            // Recaptcha unavailable — continue without blocking subscription
        }

        const formData = new FormData();
        formData.append('email', email.trim());

        startTransition(async () => {
            try {
                const result = await subscribeNewsLetter(context.companyId as string, formData);

                if (result.success) {
                    setStatus({
                        type: 'success',
                        message: result.message || 'Thanks for subscribing.',
                    });
                    setEmail('');
                } else if (result.alreadySubscribed) {
                    setStatus({
                        type: 'info',
                        message: result.message || 'You are already subscribed to our newsletter.',
                    });
                } else {
                    setStatus({
                        type: 'error',
                        message: result.message || 'Unable to subscribe right now.',
                    });
                }
            } catch {
                setStatus({
                    type: 'error',
                    message: 'Unable to subscribe right now.',
                });
            } finally {
                setIsPending(false);
            }
        });
    };

    return (
        <div style={{ width: '100%' }}>
            {/*
              Matches theme-builder's renderNode.tsx NEWSLETTER_FORM case
              exactly: a flat <form> containing <input> + <button> directly,
              no extra wrapper div. The form's own look (background, border,
              radius) comes entirely from `inlineStyles` (the node's own
              `styles`, set in footerStructure.ts/contentStructure.ts) —
              never a hardcoded shape here, so canvas/Preview/storefront
              render byte-for-byte the same box.
            */}
            <form
                onSubmit={handleSubmit}
                style={{
                    ...inlineStyles,
                    alignItems: 'stretch',
                }}
                className={['cb-newsletter-form', customClassName].filter(Boolean).join(' ')}
            >
                <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={inputPlaceholder}
                    aria-label="Email address"
                    disabled={isPending}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: inlineStyles.color as string | undefined,
                        padding: '0 14px',
                    }}
                />
                <button
                    type="submit"
                    disabled={isPending}
                    style={{
                        border: 'none',
                        outline: 'none',
                        cursor: isPending ? 'not-allowed' : 'pointer',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        padding: '0 24px',
                        fontSize: '14px',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        opacity: isPending ? 0.75 : 1,
                        ...buttonStyles,
                    }}
                >
                    {isPending ? 'Submitting...' : submitLabel}
                </button>
            </form>

            {status.message ? (
                <p
                    style={{
                        marginTop: '12px',
                        marginBottom: 0,
                        fontSize: '13px',
                        color:
                            status.type === 'success'
                                ? '#15803d'
                                : status.type === 'info'
                                  ? '#2563eb'
                                  : '#dc2626',
                        textAlign: 'center',
                    }}
                >
                    {status.message}
                </p>
            ) : null}
        </div>
    );
};

export default NewsletterFormComponent;
